import { useMemo } from 'react';
import { concat, isEmpty } from 'lodash-es';
import { useIntl } from 'umi';
import type { ChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { getTemplateFlags } from '@/pages/aigc/utils/utils';
import aiExcelIcon from '@/pages/aigc/assets/svg/agent-ai-excel.svg';
import magicPosterIcon from '@/pages/aigc/assets/svg/agent-ai-image-generation.svg';

/** ChatPage 模板配置 Hook 入参 */
export interface IUseChatPageTemplateConfigParams {
  /** URL template（dsReport / aiExcel / magicPoster / skill） */
  template?: string;
  /** 会话级模板模式（历史 Skill 会话） */
  sessionTemplateMode: string;
  /** 当前会话 ID */
  sessionId?: string;
  /** 会话列表 */
  chatList?: ChatList[];
  /** 收藏会话列表 */
  bookMarkedChats?: ChatList[];
  /** 首页带入的 Agent 名称（DS 报告兜底标题） */
  agentName?: string;
  /** 首页带入的 Agent 横幅（DS 报告兜底图） */
  agentBanner?: string;
}

/** 输入框上方模板标题（AI 表格 / 魔法海报） */
export interface IChatPageTemplateHeaderConfig {
  /** 标题文案；非表格/海报时为空串 */
  name: string;
  /** 标题图标；非表格/海报时为空串 */
  icon: string;
}

/** DS 报告横幅 / 输入框标题用的会话摘要 */
export interface IChatPageDsSelectedSample {
  /** 报告 / Agent 标题 */
  agent_title?: string;
  /** 报告横幅图 */
  agent_banner?: string;
}

/** ChatPage 模板配置 Hook 返回值 */
export interface IUseChatPageTemplateConfigResult {
  /** 是否 DS 报告模式 */
  isDsMode: boolean;
  /** 是否 AI 表格模式 */
  isExcel: boolean;
  /** 是否魔法海报模式 */
  isMagicPoster: boolean;
  /** 是否 Skill 会话 */
  isSkill: boolean;
  /** 输入框上方表格/海报标题配置 */
  templateConfig: IChatPageTemplateHeaderConfig;
  /** DS 报告标题与横幅（非 dsReport 时为空对象） */
  initSelectedSample: IChatPageDsSelectedSample;
}

/**
 * ChatPage 模板展示配置：模板标志、输入框标题、DS 报告横幅。
 * @param params - 路由 template、会话列表与首页带入的 Agent 信息
 * @returns 模板标志与展示配置
 */
export const useChatPageTemplateConfig = ({
  template,
  sessionTemplateMode,
  sessionId,
  chatList,
  bookMarkedChats,
  agentName,
  agentBanner,
}: IUseChatPageTemplateConfigParams): IUseChatPageTemplateConfigResult => {
  const { formatMessage } = useIntl();
  const { isDsMode, isExcel, isMagicPoster, isSkill } = getTemplateFlags(
    template,
    sessionTemplateMode,
  );

  const mergedChatList = useMemo(() => {
    return concat(chatList, bookMarkedChats);
  }, [chatList, bookMarkedChats]);

  const initSelectedSample = useMemo((): IChatPageDsSelectedSample => {
    if (template !== 'dsReport') return {};
    let data: IChatPageDsSelectedSample = {};
    const chatMenuList = mergedChatList?.find((task) => task?.session_id === sessionId);
    if (!isEmpty(chatMenuList)) {
      data = chatMenuList as IChatPageDsSelectedSample;
    } else if (agentName) {
      data = {
        agent_title: agentName,
        agent_banner: agentBanner,
      };
    }
    return data;
  }, [agentBanner, agentName, mergedChatList, sessionId, template]);

  const templateConfig = useMemo((): IChatPageTemplateHeaderConfig => {
    if (isExcel) {
      return {
        name: formatMessage({ id: 'dba_chat_type_ai_table' }),
        icon: aiExcelIcon,
      };
    }
    if (isMagicPoster) {
      return {
        name: formatMessage({ id: 'dba_chat_type_magic_poster' }),
        icon: magicPosterIcon,
      };
    }
    return { name: '', icon: '' };
  }, [isExcel, isMagicPoster, formatMessage]);

  return {
    isDsMode,
    isExcel,
    isMagicPoster,
    isSkill,
    templateConfig,
    initSelectedSample,
  };
};
